import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { labService } from '../services/labService';
import SafeIcon from '@/common/SafeIcon';

const AgentRegistry = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [filterActive, setFilterActive] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let currentAgents = [];

    labService.getAgents().then(agents => {
      currentAgents = agents;
      setAgents(agents);
    });
    labService.getOrgStats().then(setStats);

    const fetchTasks = async () => {
      const tasks = await labService.getTasks();
      updateAgentStatus(tasks);
    };

    const updateAgentStatus = (tasks) => {
      setAgents(prevAgents => {
        return prevAgents.map(agent => {
          const isActive = tasks.some(t => {
            const statusMatch = ['Generating', 'Committing', 'Review Gate'].includes(t.status);
            const modelMatch = t.assigned_model === agent.model || t.context?.assigned_model === agent.model || (!t.assigned_model && !t.context?.assigned_model);
            return statusMatch && modelMatch;
          });

          return {
            ...agent,
            status: isActive ? 'ACTIVE' : 'IDLE'
          };
        });
      });
    };

    fetchTasks();

    const unsubscribe = labService.subscribe((event) => {
      if (event.type === 'TASKS_UPDATED') {
        updateAgentStatus(event.tasks);
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Swarm Personnel</h1>
          <p className="text-sm text-gray-400 mt-1">Status and performance of specialized autonomous agents</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex bg-slate-900/90 p-1 rounded-lg border border-slate-800">
            <button
              onClick={() => setFilterActive(false)}
              className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest rounded-md transition-all ${!filterActive ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'text-gray-500 hover:text-gray-300 border border-transparent'}`}
            >
              All Agents
            </button>
            <button
              onClick={() => setFilterActive(true)}
              className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest rounded-md transition-all ${filterActive ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'text-gray-500 hover:text-gray-300 border border-transparent'}`}
            >
              Active Only
            </button>
          </div>
          <div className="flex gap-4">
            <MetricSmall label="ACTIVE_SWARM" value={stats?.activeSwarmSize || 0} />
            <MetricSmall label="SUCCESS_RATE" value={stats?.prSuccessRate || '0%'} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {agents.filter(agent => !filterActive || agent.status === 'ACTIVE').map((agent, idx) => (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            key={agent.id}
            className={`bg-slate-900/90 border ${agent.isPrimary ? 'border-cyan-500/50 shadow-[0_0_15px_rgba(6,182,212,0.15)]' : 'border-slate-800'} rounded-2xl p-6 relative overflow-hidden group hover:border-blue-500/30 transition-all`}
          >
            <div className={`absolute top-0 right-0 w-32 h-32 ${agent.status === 'ACTIVE' ? 'bg-blue-500/5' : 'bg-gray-500/5'} blur-3xl rounded-full`}></div>
            {agent.isPrimary && <div className="absolute top-0 right-0 px-2 py-1 bg-cyan-500/20 text-cyan-400 text-[8px] font-bold tracking-widest uppercase rounded-bl-lg border-b border-l border-cyan-500/30">Primary Core</div>}
            
            <div className="flex items-center justify-between mb-6 relative z-10">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${
                  agent.status === 'ACTIVE' ? 'bg-blue-500/10 border-blue-500/30' : 'bg-gray-500/10 border-gray-500/30'
                }`}>
                  <SafeIcon name="Cpu" className={agent.status === 'ACTIVE' ? 'text-blue-400 animate-pulse' : 'text-gray-400'} />
                </div>
                <div>
                  <h3 className="text-white font-bold group-hover:text-blue-400 transition-colors uppercase tracking-tight">{agent.name}</h3>
                  <p className="text-[10px] text-gray-500 font-mono uppercase tracking-widest">{agent.role}</p>
                </div>
              </div>
              <div className={`w-2 h-2 rounded-full ${agent.status === 'ACTIVE' ? 'bg-blue-500 animate-pulse shadow-[0_0_8px_rgba(59,130,246,0.5)]' : 'bg-gray-600'}`}></div>
            </div>

            <div className="space-y-4 relative z-10">
              <div className="grid grid-cols-2 gap-4">
                <AgentStat label="UPTIME" value={agent.uptime} />
                <AgentStat label="TASKS" value={agent.tasks_completed} />
              </div>
              
              <div className="pt-4 border-t border-slate-800">
                {agent.isPrimary && (
                  <div className="mb-4 space-y-2">
                    <p className="text-[10px] text-cyan-500 font-mono uppercase tracking-widest">Core Telemetry</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-cyan-950/30 p-2 rounded border border-cyan-900/50">
                        <p className="text-[8px] text-gray-400 font-mono uppercase mb-0.5">Active Queue</p>
                        <p className="text-xs text-cyan-400 font-bold">12 Tasks</p>
                      </div>
                      <div className="bg-cyan-950/30 p-2 rounded border border-cyan-900/50">
                        <p className="text-[8px] text-gray-400 font-mono uppercase mb-0.5">PRs Opened (Wk)</p>
                        <p className="text-xs text-cyan-400 font-bold">84</p>
                      </div>
                      <div className="bg-cyan-950/30 p-2 rounded border border-cyan-900/50">
                        <p className="text-[8px] text-gray-400 font-mono uppercase mb-0.5">Avg Review Time</p>
                        <p className="text-xs text-cyan-400 font-bold">4.2s</p>
                      </div>
                      <div className="bg-cyan-950/30 p-2 rounded border border-cyan-900/50">
                        <p className="text-[8px] text-gray-400 font-mono uppercase mb-0.5">Token Efficiency</p>
                        <p className="text-xs text-emerald-400 font-bold">A+</p>
                      </div>
                    </div>
                  </div>
                )}
                <p className="text-[10px] text-gray-500 font-mono uppercase mb-3 tracking-widest">Cognitive Stack</p>
                <div className="space-y-2">
                  <div className="flex justify-between text-[11px] font-mono">
                    <span className="text-gray-600">MODEL</span>
                    <span className="text-gray-300">{agent.model}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 pt-2">
                    {agent.capabilities.map(cap => (
                      <span key={cap} className="px-2 py-1 bg-gray-800/50 text-gray-400 text-[9px] rounded font-mono border border-slate-800 group-hover:border-gray-700 transition-colors">
                        {cap}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <button onClick={() => navigate('/audit', { state: { actorFilter: agent.name } })} className="w-full py-2.5 bg-gray-900 hover:bg-blue-600 hover:text-white text-gray-400 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all border border-slate-800 mt-2">
                View Mission Logs
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

const MetricSmall = ({ label, value }) => (
  <div className="bg-slate-900/90 border border-slate-800 rounded-lg px-4 py-2 text-center">
    <p className="text-[9px] text-gray-500 font-mono uppercase tracking-widest mb-0.5">{label}</p>
    <p className="text-lg font-bold text-white">{value}</p>
  </div>
);

const AgentStat = ({ label, value }) => (
  <div className="bg-gray-900/50 rounded-lg p-3 border border-slate-800/50">
    <p className="text-[9px] text-gray-500 font-mono uppercase tracking-tighter mb-1">{label}</p>
    <p className="text-sm font-bold text-gray-200">{value}</p>
  </div>
);

export default AgentRegistry;